import { useEffect, useState } from "react"
import { listAdminConfig, updateAdminConfig, type RuntimeConfigEntry } from "../api/admin.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { Input } from "../components/ui/Input.js"
import { Switch } from "../components/ui/Switch.js"
import { useToast } from "../components/ui/Toast.js"
import { InfraStatusBanner } from "../components/infra/InfraStatusBanner.js"
import { Coachmark } from "../components/onboarding/Coachmark.js"
import { useFirstVisit } from "../components/onboarding/useFirstVisit.js"
import { getHint } from "../components/onboarding/hints.js"
import type { InfraStatus } from "@mc/types"

type PageState =
  | { status: "loading" }
  | { status: "ready"; entries: RuntimeConfigEntry[]; infraStatus: InfraStatus | null }
  | { status: "error"; message: string }

/**
 * FR seccion 8/19 item 18 (/admin, exclusivo de host_owner). Cubre lo que
 * hoy tiene backend real: estado de infra (GET /api/v1/infra/status, ya
 * usado en el dashboard) y el hot reload de runtime_config (PR seccion
 * 6.1, GET/PATCH /api/v1/admin/config, AGENT.md decisiones 21-22). El
 * resto de FR seccion 19 item 18 (ver todos los servidores del host,
 * gestion de infraestructura mas alla de config) no tiene endpoint
 * todavia — no se fabrica, se deja para cuando exista.
 */
export function AdminPage() {
  const [state, setState] = useState<PageState>({ status: "loading" })
  const toast = useToast()
  const [isFirstVisit, dismissFirstVisit] = useFirstVisit("admin-runtime-config")

  useEffect(() => {
    load()
  }, [])

  function load() {
    setState({ status: "loading" })
    listAdminConfig()
      .then(({ entries, infraStatus }) => setState({ status: "ready", entries, infraStatus }))
      .catch((err) => setState({ status: "error", message: friendlyError(err).message }))
  }

  if (state.status === "loading") {
    return (
      <div style={{ display: "grid", placeItems: "center", padding: "var(--space-12) 0" }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (state.status === "error") {
    return (
      <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
        {state.message}
      </p>
    )
  }

  const { entries, infraStatus } = state

  async function handleSave(key: string, value: unknown) {
    try {
      await updateAdminConfig(key, value)
      toast.show("Configuración actualizada. Los servicios la toman sin reiniciar.", "success")
      load()
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    }
  }

  const configSection = (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <h2 style={{ fontSize: "var(--text-2xl)" }}>Configuración en vivo</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {entries.map((entry) => (
          <ConfigRow key={entry.key} entry={entry} onSave={(value) => handleSave(entry.key, value)} />
        ))}
      </div>
    </section>
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-8)" }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>Admin</h1>
        <p style={{ color: "var(--text-muted)" }}>Estado de infraestructura y configuración del sistema.</p>
      </div>

      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <h2 style={{ fontSize: "var(--text-2xl)" }}>Infraestructura</h2>
        <InfraStatusBanner infraStatus={infraStatus} />
        {infraStatus && (
          <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
            Último chequeo: {new Date(infraStatus.checkedAt).toLocaleString()}
          </p>
        )}
        {!infraStatus && (
          <p style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
            Todavía no hay ningún chequeo de infraestructura registrado.
          </p>
        )}
      </section>

      {isFirstVisit ? (
        <Coachmark title={getHint("admin-runtime-config").title} description={getHint("admin-runtime-config").description} onDismiss={dismissFirstVisit}>
          {configSection}
        </Coachmark>
      ) : (
        configSection
      )}
    </div>
  )
}

function ConfigRow({ entry, onSave }: { entry: RuntimeConfigEntry; onSave: (value: unknown) => Promise<void> }) {
  const isBoolean = typeof entry.value === "boolean" || entry.key === "EXCLUSIVE_MODE"
  const [draft, setDraft] = useState(entry.value == null ? "" : String(entry.value))
  const [saving, setSaving] = useState(false)

  const dirty = !isBoolean && draft !== (entry.value == null ? "" : String(entry.value))

  async function save(value: unknown) {
    setSaving(true)
    try {
      await onSave(value)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--space-4)",
        flexWrap: "wrap",
        padding: "var(--space-4)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-default)",
        background: "var(--bg-surface)",
        transition: `border-color var(--duration-small) var(--ease-out)`,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", flex: "1 1 260px" }}>
        <strong style={{ fontSize: "var(--text-sm)" }}>{entry.key}</strong>
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{entry.description}</span>
        {entry.value == null && (
          <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
            Sin definir todavía, usando el valor por defecto del servicio.
          </span>
        )}
      </div>

      {isBoolean ? (
        <Switch
          label={entry.key}
          checked={entry.value === true}
          loading={saving}
          onChange={(next) => save(next)}
        />
      ) : (
        <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-2)" }}>
          <div style={{ width: 160 }}>
            <Input
              label=""
              aria-label={entry.key}
              type="number"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            loading={saving}
            disabled={!dirty || draft === ""}
            onClick={() => save(Number(draft))}
          >
            Guardar
          </Button>
        </div>
      )}
    </div>
  )
}
